import { Components, Theme } from '@mui/material'

// MuiPaper already gives every Paper the surface colour, border and radius, and Card is a Paper.
export const MuiCard: Components<Omit<Theme, 'components'>>['MuiCard'] = {}

export const MuiCardHeader: Components<Omit<Theme, 'components'>>['MuiCardHeader'] = {
  styleOverrides: {
    subheader: ({ theme }) => ({
      color: theme.palette.text.muted,
    }),
  },
}

export const MuiCardContent: Components<Omit<Theme, 'components'>>['MuiCardContent'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      // MUI singles out the last child for 24px, which reads oddly against its 16px sides. Note this
      // selector outranks a plain `sx` class, so a caller wanting a different bottom has to
      // re-declare `&:last-child` rather than just setting `padding`.
      '&:last-child': {
        paddingBottom: theme.spacing(4),
      },
    }),
  },
}

export const MuiCardActions: Components<Omit<Theme, 'components'>>['MuiCardActions'] = {}

export const MuiCardActionArea: Components<Omit<Theme, 'components'>>['MuiCardActionArea'] = {
  defaultProps: {
    disableRipple: true,
  },
  styleOverrides: {
    root: {
      // The overlay token already carries its own alpha, so it is shown at full opacity rather than
      // being multiplied by MUI's hoverOpacity/focusOpacity, which would leave it imperceptible.
      // Both of MUI's rules need this: hover, and focus-visible — the latter is the only focus
      // affordance a card has, since ButtonBase clears the outline.
      '&:hover .MuiCardActionArea-focusHighlight': {
        opacity: 1,
        // Restores MUI's guard, which would otherwise be lost: :hover latches on touch
        '@media (hover: none)': {
          opacity: 0,
        },
      },
      '&.Mui-focusVisible .MuiCardActionArea-focusHighlight': {
        opacity: 1,
      },
    },
    focusHighlight: ({ theme }) => ({
      backgroundColor: theme.palette.bg.interactive.hover,
    }),
  },
}

// A placeholder surface until the asset loads, inset from the card edge. The radius is derived from the
// card's own so the two curves stay concentric: an inset box needs its container's radius less the
// inset, and matching the card exactly would read as rounder — which is why the inset lives here rather
// than with the caller, so the two can't drift apart. Zero the one edge that faces the content, since
// `CardContent`'s padding is already the gap there. See the Card stories.
export const MuiCardMedia: Components<Omit<Theme, 'components'>>['MuiCardMedia'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      backgroundColor: theme.palette.bg.surface.subtle,
      margin: theme.spacing(1),
      borderRadius: `calc(var(--paper-border-radius, ${theme.shape['radius-md']}) - ${theme.spacing(1)})`,
    }),
  },
}
